// structuralItems.js
// Structural recertification: Photo Manager sections, their form lines, the
// site systems checklist and photo numbering, per jurisdiction.
//
// Form layouts:
//   Miami-Dade and City of Miami share one form (sections 4–14 here).
//   Boca Raton has its own (4–14, with 7a1–7a13, post-tension 9f, added loads 13c).
//   Broward is ordered differently (2 Present Condition, 5 Foundation … 13) and has
//   no garage section.
// Sections 1–3 and 15–20 are report-level and live on Report Details.
//
// Line ids follow the form item numbers, so they line up with the template field
// names (line 6a → fields s6a / s6a_cond). Line kinds:
//   rating  — condition buttons (Good / Fair / Poor / Significant [/ N/A]), optional text
//   select  — one of a fixed list, optional text
//   yesno   — Yes / No (or the form's own list), optional text
//   radio   — one of several form lines (e.g. rebar corrosion 1–4), text for some
//   text    — free text
// Flags: opt (not required), def (default answer), naBy (site system that makes the
// line N/A), when ({ id, is } — only asked when another line has that answer),
// short (one-line input).

// ─── Option lists (match the form dropdowns) ──────────────────────────────────

const RATE   = ['Good', 'Fair', 'Poor', 'Significant'];
const RATE_N = ['Good', 'Fair', 'Poor', 'Significant', 'N/A'];
const RATE3  = ['Good', 'Fair', 'Poor'];
const YN     = ['Yes', 'No'];
const YN_ATT = ['Yes', 'No', 'Needs Attention'];
const DATUM  = ['NGVD29', 'NAVD88'];

// ─── Line constructors ────────────────────────────────────────────────────────

const R  = (id, label, o = {}) => ({ id, label, kind: 'rating', options: o.options || RATE, text: o.text !== false, ...o });
const S  = (id, label, options, o = {}) => ({ id, label, kind: 'select', options, text: !!o.text, ...o });
const Y  = (id, label, o = {}) => ({ id, label, kind: 'yesno', options: o.options || YN, text: !!o.text, ...o });
const T  = (id, label, o = {}) => ({ id, label, kind: 'text', ...o });
const RD = (id, label, options, o = {}) => ({ id, label, kind: 'radio', options, ...o });   // options: [{ v, text }]

// ─── Section captions (photo report and Photo Manager headings) ───────────────

const CAP = {
  foundation: 'Foundation',
  present:    'Present Condition of Overall Structure',
  masonry:    'Masonry Bearing Wall',
  floorRoof:  'Floor and Roof System',
  steel:      'Steel Framing System',
  concrete:   'Concrete Framing System',
  windows:    'Windows, Storefronts and Exterior Doors',
  wood:       'Wood Framing',
  facade:     'Building Façade',
  special:    'Special Features',
  garage:     'Underground Parking Garage',
};

// ─── Miami-Dade / City of Miami ───────────────────────────────────────────────

const MIAMI = [
  { num: '4', title: CAP.foundation, lines: [
    T('4a', 'a. Describe the building foundation (visual observation, type of construction or existing plans)'),
    Y('4b', 'b. Is wood in contact or near soil?', { options: YN_ATT, text: true }),
    Y('4c', 'c. Signs of differential settlement?', { options: YN_ATT, text: true }),
    T('4d', 'd. Cracks or separation in walls, columns or beams that signal differential settlement'),
    Y('4e', 'e. Is water drained away from the foundation?', { options: ['Yes', 'No', 'Needs Repair'] }),
    Y('4f', 'f. Is additional sub-soil investigation required?'),
    T('4f1', 'f1. Describe', { when: { id: '4f', is: 'Yes' } }),
  ]},
  { num: '5', title: CAP.present, lines: [
    R('5a1', 'a1. Bulging', { def: 'Good' }),
    R('5a2', 'a2. Settlement', { def: 'Good' }),
    R('5a3', 'a3. Deflections', { def: 'Good' }),
    R('5a4', 'a4. Expansion', { def: 'Good' }),
    R('5a5', 'a5. Contraction', { def: 'Good' }),
    T('5b', 'b. Portion showing distress (beams, columns, structural walls, floor, roofs, other)'),
    T('5c', 'c. Surface conditions (finishes, cracking, spalling, peeling, moisture penetration, stains)'),
    S('5d', 'd. Cracks — size and location in significant members', ['Hairline (Barely Discernable)', 'Fine (Less than 1 mm)', 'Medium (1 - 2 mm)', 'Wide (Over 2 mm)'], { text: true, opt: true }),
    T('5e', 'e. General extent of deterioration'),
    T('5f', 'f. Previous patching or repairs (description and location)'),
    S('5g', 'g. Nature of present loading', ['Assembly', 'Industrial', 'Institutional', 'Office', 'Parking', 'Residential', 'Restaurant', 'Storage', 'Other-Identify'], { text: true }),
    Y('5h', 'h. Any other significant observations?'),
    T('5h1', 'h1. Describe', { when: { id: '5h', is: 'Yes' } }),
  ]},
  { num: '6', title: CAP.masonry, naBy: 'masonry', lines: [
    R('6a', 'a. Concrete masonry units', { options: RATE_N }),
    R('6b', 'b. Clay tile or terra cotta units', { options: RATE_N }),
    R('6c', 'c. Reinforced concrete tie columns', { options: RATE_N }),
    R('6d', 'd. Reinforced concrete tie beams', { options: RATE_N }),
    R('6e', 'e. Lintel', { options: RATE_N }),
    R('6f', 'f. Other type bond beams', { options: RATE_N }),
    R('6g1', 'g1. Exterior finish — stucco', { options: RATE_N }),
    R('6g2', 'g2. Exterior finish — veneer', { options: RATE_N }),
    R('6g3', 'g3. Exterior finish — paint only', { options: RATE_N }),
    R('6g4', 'g4. Exterior finish — other (describe)', { options: RATE_N }),
    R('6h1', 'h1. Interior finish — vapor barrier', { options: RATE_N }),
    R('6h2', 'h2. Interior finish — furring and plaster', { options: RATE_N }),
    R('6h3', 'h3. Interior finish — paneling', { options: RATE_N }),
    R('6h4', 'h4. Interior finish — paint only', { options: RATE_N }),
    R('6h5', 'h5. Interior finish — other (describe)', { options: RATE_N }),
    T('6i1', 'i1. Cracks — location (beams, columns, other)'),
    T('6i2', 'i2. Cracks — description'),
    S('6j', 'j. Spalling', ['As Noted Below', 'None Observed']),
    T('6j1', 'j1. Spalling — location', { when: { id: '6j', is: 'As Noted Below' } }),
    T('6j2', 'j2. Spalling — description', { when: { id: '6j', is: 'As Noted Below' } }),
    RD('6k', 'k. Rebar corrosion (worst case)', [
      { v: 'None visible' }, { v: 'Minor (patching will suffice)', text: true },
      { v: 'Significant (but patching will suffice)', text: true }, { v: 'Significant (structural repairs required)', text: true },
    ]),
    Y('6l', 'l. Samples chipped out for examination in spalled areas?'),
    T('6l1', 'l1. Color, texture, aggregate, general quality', { when: { id: '6l', is: 'Yes' } }),
  ]},
  { num: '7', title: CAP.floorRoof, lines: [
    S('7a1_pitch', 'a1. Roof pitch', ['Flat (<2:12)', 'Pitched (>=2:12)']),
    S('7a1_cladding', 'a1. Roof cladding type', ['Built-up', 'Asphalt Shingles', 'Tile', 'Single Ply', 'Metal', 'Other'], { text: true }),
    S('7a1_deck', 'a1. Roof deck material', ['Wood', 'Steel Deck', 'Reinforced Concrete', 'LWC on Steel Deck', 'Conc on Steel Deck', 'Other'], { text: true }),
    S('7a1_framing', 'a1. Roof structural framing type', ['Wood', 'Concrete', 'Steel', 'Other', 'Unknown'], { text: true }),
    R('7a1', 'a1. Roof structural framing condition'),
    T('7a2', 'a2. Water tanks, cooling towers, A/C equipment, signs, heavy equipment and supports', { naBy: 'rooftop' }),
    T('7a3', 'a3. Roof drainage system, main and overflow, and condition'),
    T('7a4', 'a4. Parapet construction and condition', { naBy: 'parapet' }),
    R('7a5', 'a5. Mansard construction and condition', { naBy: 'mansard' }),
    R('7a6', 'a6. Roofing membrane / covering and condition'),
    T('7a7', 'a7. Roof framing members with overloading, overstress, deterioration or excessive deflection'),
    R('7a8', 'a8. Expansion joints and condition'),
    R('7b1', 'b1. Floor system at each level (framing, material, spans, condition)'),
    S('7b2_type', 'b2. Balconies — construction', ['Concrete', 'Wood', 'Conc on Steel', 'No Balcony', 'Other'], { naBy: 'balconies', naValue: 'No Balcony' }),
    R('7b2', 'b2. Balconies — condition', { naBy: 'balconies' }),
    T('7b2_location', 'b2. Balconies — location', { naBy: 'balconies', short: true }),
    T('7b3', 'b3. Stairs and escalators (location, framing, material, condition)', { naBy: 'stairs' }),
    T('7b4', 'b4. Ramps (location, framing, material, condition)', { naBy: 'ramps' }),
    T('7b5', 'b5. Guardrails and handrails (type, material, condition)'),
    T('7c', 'c. Inspection — areas exposed, and where ceilings etc. were opened'),
  ]},
  { num: '8', title: CAP.steel, naBy: 'steel', lines: [
    T('8a', 'a. Description of system at each level'),
    S('8b', 'b. Exposed steel members — paint and corrosion', ['Not Significant', 'Significant but treatment will suffice', 'Significant requires repair'], { text: true }),
    S('8c', 'c. Steel connections — type and condition', ['Not Significant', 'Significant but treatment will suffice', 'Significant requires repair'], { text: true }),
    T('8d', 'd. Concrete or other fireproofing — cracking or spalling; covering removed'),
    T('8e', 'e. Steel members with overloading, overstress, deterioration or excessive deflection'),
    T('8f', 'f. Elevator sheave beams, connections and machine floor beams', { naBy: 'elevator' }),
  ]},
  { num: '9', title: CAP.concrete, naBy: 'concrete', lines: [
    T('9a', 'a. Full description of concrete structural framing system'),
    RD('9b', 'b. Cracking', [{ v: 'Not Significant' }, { v: 'Significant but patching will suffice' }, { v: 'Significant: structural repairs required' }]),
    T('9b4', 'b4. Location and description of members affected and type of cracking'),
    T('9c', 'c. General condition'),
    RD('9d', 'd. Rebar corrosion', [
      { v: 'None visible' }, { v: 'Location and description of members affected', text: true },
      { v: 'Significant but patching will suffice', text: true }, { v: 'Significant: structural repairs required', text: true },
    ]),
    Y('9e', 'e. Samples chipped out in spall areas?'),
    T('9e2', 'e2. Color, texture, aggregate, general quality', { when: { id: '9e', is: 'Yes' } }),
    T('9f', 'f. Concrete members with overloading, overstress, deterioration or excessive deflection'),
  ]},
  { num: '10', title: CAP.windows, lines: [
    T('10a1', 'a1. Windows / storefronts / curtain walls — type'),
    R('10a2', 'a2. Anchorage — fasteners and latches'),
    R('10a3', 'a3. Sealant — perimeter and mullions'),
    R('10a4', 'a4. Interior seals at operable vents'),
    R('10a5', 'a5. General condition'),
    T('10a6', 'a6. Repairs needed'),
    Y('10b', 'b. Structural glazing on the exterior envelope (threshold buildings)?', { naBy: 'threshold', naValue: 'No' }),
    T('10b1', 'b1. Previous inspection date', { when: { id: '10b', is: 'Yes' }, short: true }),
    T('10b2', 'b2. Curtain wall structural glazing and adhesive sealant', { when: { id: '10b', is: 'Yes' } }),
    T('10b3', 'b3. Condition of system', { when: { id: '10b', is: 'Yes' } }),
    T('10c1', 'c1. Exterior doors — type'),
    R('10c2', 'c2. Exterior doors — anchorage, fasteners and latches'),
    R('10c3', 'c3. Exterior doors — sealant'),
    R('10c4', 'c4. Exterior doors — general condition'),
    S('10c5', 'c5. Exterior doors — repairs needed', ['Repairs Required', 'Repairs Not Required'], { text: true }),
  ]},
  { num: '11', title: CAP.wood, naBy: 'wood', lines: [
    T('11a', 'a. Type (mill construction, light construction, major spans, trusses)'),
    R('11b1', 'b1. Walls'),
    R('11b2', 'b2. Floors'),
    R('11b3', 'b3. Roof members, roof trusses'),
    R('11c', 'c. Metal connectors (angles, plates, bolts, split pintles)'),
    T('11d', 'd. Joints — well fitted and still closed'),
    T('11e', 'e. Drainage — accumulations of moisture'),
    T('11f', 'f. Ventilation — concealed spaces not ventilated'),
    T('11g', 'g. Concealed spaces opened for inspection'),
    T('11h', 'h. Wood members with overloading, overstress, deterioration or excessive deflection'),
  ]},
  { num: '12', title: CAP.facade, naBy: 'threshold', lines: [
    T('12a', 'a. Exterior walls and appurtenances on all sides (cladding, corbels, precast appliqués)'),
    T('12b', 'b. Attachment type of each appurtenance (mechanically attached or adhered)'),
    T('12c', 'c. Condition of each appurtenance'),
  ]},
  { num: '13', title: CAP.special, naBy: 'special', lines: [
    T('13a', 'a. Special or unusual features'),
    T('13b', 'b. Condition of the feature, supports and connections; repairs required'),
  ]},
  { num: '14', title: CAP.garage, naBy: 'garage', lines: [
    T('14a_bfe', 'Current base flood elevation (ft)', { short: true }),
    S('14a_bfe_datum', 'Base flood elevation — datum', DATUM),
    T('14a1', '1. Wet season ground water elevation (ft)', { short: true }),
    S('14a1_datum', '1. Datum', DATUM),
    T('14a2', '2. Lowest parking garage finished floor elevation (ft)', { short: true }),
    S('14a2_datum', '2. Datum', DATUM),
    T('14a3', '3. Parking garage entrance elevation (ft)', { short: true }),
    S('14a3_datum', '3. Datum', DATUM),
    Y('14a4', '4. Wet season water table higher than lowest floor?', { text: true }),
    Y('14a5', '5. Garage entrance lower than base flood elevation?', { text: true }),
    T('14a6', '6. Use of structure above the underground garage'),
    T('14a7', '7. Evidence of bulging, settlement, cracking or deflection'),
    T('14a8', '8. General surface conditions'),
    Y('14b1', '14B-1. Evidence of leakage at slabs or walls?', { text: true }),
    Y('14b2', '14B-2. Evidence of previous patching or repairs?', { text: true }),
  ]},
];

// ─── Boca Raton ───────────────────────────────────────────────────────────────

const BOCA = [
  { num: '4', title: CAP.foundation, lines: [
    T('4a', 'a. Describe the building foundation (visual observation, type of construction or existing plans)'),
    Y('4b', 'b. Signs of differential settlement?'),
    T('4c', 'c. Cracks or separation in walls, columns or beams that signal differential settlement'),
    T('4d', 'd. Slab at grade'),
    Y('4e', 'e. Is wood in contact or near soil?'),
    Y('4f', 'f. Is additional sub-soil investigation required?'),
    T('4f1', 'f. If yes, explain', { when: { id: '4f', is: 'Yes' } }),
    Y('4g', 'g. Is water drained away from the foundation?'),
  ]},
  { num: '5', title: CAP.present, lines: [
    R('5a1', 'a1. Bulging', { def: 'Good', text: false }),
    R('5a2', 'a2. Settlement', { def: 'Good', text: false }),
    R('5a3', 'a3. Deflections', { def: 'Good', text: false }),
    R('5a4', 'a4. Expansion', { def: 'Good', text: false }),
    R('5a5', 'a5. Contraction', { def: 'Good', text: false }),
    T('5b', 'b. Portion showing distress (beams, columns, structural walls, floor, roofs, other)'),
    T('5c', 'c. Surface conditions (finishes, cracking, spalling, peeling, moisture penetration, stains)'),
    T('5d', 'd. Cracks — location in significant members and size'),
    T('5e', 'e. General extent of deterioration'),
    T('5f', 'f. Previous patching or repairs (description and location)'),
    T('5g', 'g. Nature of present loading'),
    Y('5h', 'h. Any other significant observations?'),
    T('5h1', 'h. Describe', { when: { id: '5h', is: 'Yes' } }),
  ]},
  { num: '6', title: CAP.masonry, naBy: 'masonry', lines: [
    R('6a', 'a. Concrete masonry units', { options: RATE_N }),
    R('6b', 'b. Clay tile or terra cotta units', { options: RATE_N }),
    R('6c', 'c. Reinforced concrete tie columns', { options: RATE_N }),
    R('6d', 'd. Reinforced concrete tie beams', { options: RATE_N, text: false }),
    R('6e', 'e. Lintel', { options: RATE_N, text: false }),
    R('6f', 'f. Other type bond beams', { options: RATE_N, text: false }),
    R('6g1', 'g1. Exterior finish — stucco', { options: RATE_N, text: false }),
    R('6g2', 'g2. Exterior finish — veneer', { options: RATE_N, text: false }),
    R('6g3', 'g3. Exterior finish — paint only', { options: RATE_N, text: false }),
    R('6g4', 'g4. Exterior finish — other (describe)', { options: RATE_N, text: false }),
    R('6h1', 'h1. Interior finish — vapor barrier', { options: RATE_N, text: false }),
    R('6h2', 'h2. Interior finish — furring and plaster', { options: RATE_N, text: false }),
    R('6h3', 'h3. Interior finish — paneling', { options: RATE_N, text: false }),
    R('6h4', 'h4. Interior finish — paint only', { options: RATE_N, text: false }),
    R('6h5', 'h5. Interior finish — other (describe)', { options: RATE_N }),
    T('6i1', 'i1. Cracks — location (beams, columns, other)'),
    T('6i2', 'i2. Cracks — description'),
    T('6j1', 'j1. Spalling — location (beams, columns, other)'),
    T('6j2', 'j2. Spalling — description'),
    RD('6k', 'k. Rebar corrosion', [
      { v: 'None visible' }, { v: 'Minor-patching will suffice' },
      { v: 'Significant-but patching will suffice' }, { v: 'Significant-structural repairs required' },
    ]),
    Y('6l', 'l. Samples chipped out for examination in spall areas?'),
    T('6l2', 'l2. Color, texture, aggregate, general quality', { when: { id: '6l', is: 'Yes' } }),
  ]},
  { num: '7', title: CAP.floorRoof, lines: [
    T('7a1', 'a1. Describe roof (shape, covering, deck, condition)'),
    T('7a2', 'a2. Roof pitch', { short: true }),
    T('7a3', 'a3. Roof cladding type', { short: true }),
    T('7a4', 'a4. Roof deck material', { short: true }),
    T('7a5', 'a5. Roof structural framing (wood, steel, concrete)', { short: true }),
    R('7a6', 'a6. Roof structural framing condition', { text: false }),
    T('7a7', 'a7. Water tanks, cooling towers, A/C equipment, signs, heavy equipment and supports', { naBy: 'rooftop' }),
    T('7a8', 'a8. Roof drainage system, drains and scuppers, main and overflow, condition'),
    T('7a9', 'a9. Parapet construction and condition', { naBy: 'parapet' }),
    T('7a10', 'a10. Mansard construction and condition', { naBy: 'mansard' }),
    T('7a11', 'a11. Roofing membrane / covering and condition'),
    T('7a12', 'a12. Roof framing members with overloading, overstress, deterioration or excessive deflection'),
    T('7a13', 'a13. Expansion joints and condition'),
    T('7b1', 'b1. Floor system at each level (framing, material, spans, condition)'),
    T('7b2_construction', 'b2. Balconies — construction', { naBy: 'balconies', short: true }),
    R('7b2_cond', 'b2. Balconies — condition', { naBy: 'balconies', text: false }),
    T('7b2_location', 'b2. Balconies — location', { naBy: 'balconies', short: true }),
    T('7b3', 'b3. Stairs and escalators (location, framing, material, condition)', { naBy: 'stairs' }),
    T('7b4', 'b4. Ramps (location, framing, material, condition)', { naBy: 'ramps' }),
    T('7b5', 'b5. Guardrails and handrails (type, material, condition)'),
    T('7c', 'c. Inspection — areas exposed, and where ceilings etc. were opened'),
  ]},
  { num: '8', title: CAP.steel, naBy: 'steel', lines: [
    T('8a', 'a. Full description of system at each level'),
    T('8b', 'b. Exposed steel — paint and degree of corrosion'),
    T('8c', 'c. Concrete or other fireproofing — cracking or spalling; covering removed'),
    T('8d', 'd. Elevator shaft beams, connections and machine floor beams', { naBy: 'elevator' }),
    T('8e', 'e. Steel members with overloading, overstress, deterioration or excessive deflection'),
    T('8f', 'f. Steel connections — type and condition'),
  ]},
  { num: '9', title: CAP.concrete, naBy: 'concrete', lines: [
    T('9a', 'a. Full description of structural framing system'),
    RD('9b', 'b. Cracking', [{ v: 'Not significant' }, { v: 'Significant but patching will suffice' }, { v: 'Significant: structural repairs required', text: true }]),
    T('9b4', 'b4. Location and description of members affected and type of cracking'),
    T('9c', 'c. General condition'),
    RD('9d', 'd. Rebar corrosion', [
      { v: 'None visible' }, { v: 'Location and description of members affected', text: true },
      { v: 'Significant but patching will suffice', text: true }, { v: 'Significant - structural repairs required', text: true },
    ]),
    Y('9e', 'e. Samples chipped out in spall areas?'),
    T('9e2', 'e2. Color, texture, aggregate, general quality', { when: { id: '9e', is: 'Yes' } }),
    T('9f1', 'f1. Post-tension cables — signs of visible distress or damage', { naBy: 'posttension' }),
    T('9f2', 'f2. Post-tension cables — location and description of members affected', { naBy: 'posttension' }),
    T('9g', 'g. Concrete members with overloading, overstress, deterioration or excessive deflection'),
  ]},
  { num: '10', title: CAP.windows, lines: [
    T('10a1', 'a1. Windows / storefronts / curtain walls / skylights — type'),
    T('10a2', 'a2. Anchorage — fasteners and latches'),
    T('10a3', 'a3. Sealant — perimeter and mullions'),
    T('10a4', 'a4. Interior seals at operable vents'),
    R('10a5', 'a5. General condition'),
    T('10a6', 'a6. Repairs needed'),
    Y('10b', 'b. Structural glazing on the exterior envelope (threshold buildings)?', { naBy: 'threshold', naValue: 'No' }),
    T('10b1', 'b1. Previous inspection date', { when: { id: '10b', is: 'Yes' }, short: true }),
    T('10b2', 'b2. Curtain wall structural glazing and adhesive sealant', { when: { id: '10b', is: 'Yes' } }),
    T('10b3', 'b3. Condition of systems', { when: { id: '10b', is: 'Yes' } }),
    T('10c1', 'c1. Exterior doors — type'),
    T('10c2', 'c2. Exterior doors — anchorage, fasteners and latches'),
    T('10c3', 'c3. Exterior doors — sealant'),
    R('10c4', 'c4. Exterior doors — general condition'),
    T('10c5', 'c5. Exterior doors — repairs needed'),
  ]},
  { num: '11', title: CAP.wood, naBy: 'wood', lines: [
    T('11a', 'a. Type (mill construction, light construction, major spans, trusses)'),
    T('11b', 'b. Metal fittings (angles, plates, bolts, split pintles) and condition'),
    T('11c', 'c. Joints — well fitted and still closed', { short: true }),
    T('11d', 'd. Drainage — accumulations of moisture', { short: true }),
    T('11e', 'e. Ventilation — concealed spaces not ventilated', { short: true }),
    T('11f', 'f. Concealed spaces opened for inspection', { short: true }),
    T('11g', 'g. Wood members with overloading, overstress, deterioration or excessive deflection'),
    R('11h1', 'h1. Walls'),
    R('11h2', 'h2. Floors'),
    R('11h3', 'h3. Roof members, roof trusses'),
  ]},
  { num: '12', title: CAP.facade, naBy: 'threshold', lines: [
    T('12a', 'a. Exterior walls and appurtenances on all sides (cladding, corbels, precast appliqués)'),
    T('12b', 'b. Attachment type of each appurtenance (mechanically attached or adhered)'),
    T('12c', 'c. Condition of each appurtenance'),
  ]},
  { num: '13', title: CAP.special, naBy: 'special', lines: [
    T('13a', 'a. Special or unusual features'),
    T('13b', 'b. Condition of the feature, supports and connections'),
    Y('13c', 'c. Upgrades or undesigned loads added to roof, pool decks or garages (pavers, planters)?', { naBy: null }),
    Y('13c1', 'c. If so, is there an engineer’s letter for those loads?', { when: { id: '13c', is: 'Yes' } }),
    T('13c2', 'c. Comments', { opt: true }),
  ]},
  { num: '14', title: CAP.garage, naBy: 'garage', lines: [
    T('14a_bfe', 'Current base flood elevation (ft, with datum)', { short: true }),
    T('14a1', '1. Wet season ground water elevation (ft, with datum)', { short: true }),
    T('14a2', '2. Lowest parking garage finished floor elevation (ft, with datum)', { short: true }),
    T('14a3', '3. Parking garage entrance elevation (ft, with datum)', { short: true }),
    T('14a4', '4. Wet season water table higher than lowest floor? (Yes/No, explanation)'),
    T('14a5', '5. Garage entrance lower than base flood elevation? (Yes/No, explanation)'),
    T('14a6', '6. Use of structure above the underground garage'),
    T('14a7', '7. Evidence of bulging, settlement, cracking or deflection'),
    T('14a8', '8. General surface conditions'),
    Y('14b', '14b. Evidence of leakage at slabs or walls?'),
    T('14b_txt', '14b. Explanation'),
  ]},
];

// ─── Broward ──────────────────────────────────────────────────────────────────

const BROWARD = [
  { num: '2', title: CAP.present, lines: [
    R('2a1', 'a1. Bulging', { def: 'Good' }),
    R('2a2', 'a2. Settlement', { def: 'Good' }),
    R('2a3', 'a3. Deflections', { def: 'Good' }),
    R('2a4', 'a4. Expansion', { def: 'Good' }),
    R('2a5', 'a5. Contraction', { def: 'Good' }),
    T('2b', 'b. Portion showing distress (beams, columns, structural walls, floor, roofs, other)'),
    T('2c', 'c. Surface conditions (finishes, cracking, spalling, peeling, moisture penetration, stains)'),
    T('2d', 'd. Cracks — location in significant members and size'),
    T('2e', 'e. General extent of deterioration'),
    T('2f', 'f. Previous patching or repairs'),
    T('2g', 'g. Nature of present loading'),
  ]},
  { num: '5', title: CAP.foundation, lines: [
    T('5a', 'a. Describe the building foundation'),
    T('5b', 'b. Cracks or separation in walls, columns or beams that signal differential settlement'),
    Y('5c', 'c. Is additional sub-soil investigation required?'),
    T('5c1', 'c1. If yes, explain', { when: { id: '5c', is: 'Yes' } }),
  ]},
  { num: '6', title: CAP.masonry, naBy: 'masonry', lines: [
    R('6a', 'a. Concrete masonry units', { options: RATE3, text: false }),
    R('6b', 'b. Clay tile or terra cotta units', { options: RATE3, text: false, opt: true }),
    R('6c', 'c. Reinforced concrete tie columns', { options: RATE3, text: false }),
    R('6d', 'd. Reinforced concrete tie beams', { options: RATE3, text: false }),
    R('6e', 'e. Lintel', { options: RATE3, text: false }),
    R('6f', 'f. Other type bond beams', { options: RATE3, text: false, opt: true }),
    R('6g1', 'g1. Exterior finish — stucco', { options: RATE3, text: false, opt: true }),
    R('6g2', 'g2. Exterior finish — veneer', { options: RATE3, text: false, opt: true }),
    R('6g3', 'g3. Exterior finish — paint only', { options: RATE3, text: false, opt: true }),
    R('6g4', 'g4. Exterior finish — other', { options: RATE3, text: false, opt: true }),
    T('6g4a', 'g4a. Explain', { opt: true }),
    T('6h', 'h. Cracks — beams, columns or others, including locations'),
    T('6i', 'i. Spalling — beams, columns or others, including locations'),
    RD('6j', 'j. Rebar corrosion', [
      { v: 'None Visible' }, { v: 'Minor – Patching Will Suffice' },
      { v: 'Significant – Patching Will Suffice' }, { v: 'Significant – Structural Repairs Required', text: true },
    ]),
    Y('6k', 'k. Samples chipped out for examination in spalled areas?'),
    T('6k2_txt', 'k2. Color, texture, aggregate, general quality', { when: { id: '6k', is: 'Yes' } }),
  ]},
  { num: '7', title: CAP.floorRoof, lines: [
    T('7a', 'a. Roof (summary line)', { short: true, opt: true }),
    T('7a1', 'a1. Type and condition of the current roof'),
    T('7a2', 'a2. Water tanks, cooling towers, A/C equipment, signs, heavy equipment and supports', { naBy: 'rooftop' }),
    T('7a3', 'a3. Types of drains, scuppers and condition'),
    T('7a4', 'a4. Parapet construction and condition', { naBy: 'parapet' }),
    T('7a5', 'a5. Mansard construction and condition', { naBy: 'mansard' }),
    T('7a6', 'a6. Roof framing members with overloading, overstress, deterioration or excessive deflection'),
    T('7a7', 'a7. Expansion joints and condition'),
    T('7b', 'b. Floor system(s) (summary line)', { short: true, opt: true }),
    T('7b1', 'b1. Type of system framing, material, spans and condition'),
    T('7b2', 'b2. Balconies (location, framing, material, condition)', { naBy: 'balconies' }),
    T('7b3', 'b3. Stairs and escalators (location, framing, material, condition)', { naBy: 'stairs' }),
    T('7b4', 'b4. Ramps (location, framing, material, condition)', { naBy: 'ramps' }),
    T('7b5', 'b5. Guardrails (type, location, material, condition)'),
    T('7c', 'c. Inspection (summary line)', { short: true, opt: true }),
    T('7c1', 'c1. Areas exposed, and where ceilings etc. were opened'),
  ]},
  { num: '8', title: CAP.steel, naBy: 'steel', lines: [
    T('8a', 'a. Full description of the system'),
    T('8b', 'b. Exposed steel — paint and degree of corrosion'),
    T('8c', 'c. Steel connections — type and condition'),
    T('8d', 'd. Concrete or other fireproofing — cracking or spalling; covering removed'),
    T('8e', 'e. Steel members with overloading, overstress, deterioration or excessive deflection'),
    T('8f', 'f. Elevator sheave beams, connections and machine floor beams', { naBy: 'elevator' }),
  ]},
  { num: '9', title: CAP.concrete, naBy: 'concrete', lines: [
    T('9a', 'a. Full description of the structural system'),
    RD('9b', 'b. Cracking', [{ v: 'Not Significant' }, { v: 'Significant' }]),
    T('9b2', 'b2. Members affected, location and type of cracking'),
    T('9c', 'c. General condition'),
    RD('9d', 'd. Rebar corrosion', [
      { v: 'None Visible' }, { v: 'Location and Description of Members Affected' },
      { v: 'Significant – Patching Will Suffice' }, { v: 'Significant – Structural Repairs Required', text: true },
    ]),
    Y('9e', 'e. Samples chipped out for examination in spalled areas?'),
    T('9e2_txt', 'e2. Color, texture, aggregate, general quality', { when: { id: '9e', is: 'Yes' } }),
    T('9f', 'f. Concrete members with overloading, overstress, deterioration or excessive deflection'),
  ]},
  { num: '10', title: CAP.windows, lines: [
    T('10a', 'a. Windows, storefronts and curtain walls'),
    Y('10b', 'b. Structural glazing on the exterior envelope (threshold building)?', { naBy: 'threshold', naValue: 'No' }),
    T('10b1', 'b1. Previous inspection date', { when: { id: '10b', is: 'Yes' }, short: true }),
    T('10b2', 'b2. Curtain wall structural glazing and adhesive sealant', { when: { id: '10b', is: 'Yes' } }),
    T('10b3', 'b3. Condition of system', { when: { id: '10b', is: 'Yes' } }),
    T('10c1', 'c1. Exterior doors — type'),
    T('10c2', 'c2. Exterior doors — anchorage, fasteners and latches'),
    T('10c3', 'c3. Exterior doors — sealant'),
    T('10c4', 'c4. Exterior doors — general condition'),
    T('10c5', 'c5. Exterior doors — repairs needed'),
  ]},
  { num: '11', title: CAP.wood, naBy: 'wood', lines: [
    T('11a', 'a. Type (mill construction, light construction, major spans, trusses)'),
    T('11b1', 'b1. Walls'),
    T('11b2', 'b2. Floors'),
    T('11b3', 'b3. Roof members, roof trusses'),
    T('11c', 'c. Metal fittings (angles, plates, bolts, split pintles) and condition'),
    T('11d', 'd. Joints — well fitted and still closed'),
    T('11e', 'e. Drainage — accumulations of moisture'),
    T('11f', 'f. Ventilation — concealed spaces not ventilated'),
    T('11g', 'g. Concealed spaces opened for inspection'),
    T('11h', 'h. Wood members with overloading, overstress, deterioration or excessive deflection'),
  ]},
  { num: '12', title: CAP.facade, naBy: 'threshold', lines: [
    T('12a', 'a. Exterior walls and appurtenances on all sides (cladding, corbels, precast appliqués)'),
    T('12b', 'b. Attachment type of each appurtenance (mechanically attached or adhered)'),
    T('12c', 'c. Condition of each appurtenance'),
  ]},
  { num: '13', title: CAP.special, naBy: 'special', lines: [
    T('13a', 'a. Special or unusual features'),
    T('13b', 'b. Condition of the feature, supports and connections'),
  ]},
];

// ─── Jurisdiction lookup ──────────────────────────────────────────────────────

export const structuralForm = (county) => {
  const c = (county || '').toLowerCase();
  if (c.includes('boca')) return 'boca';
  if (c.includes('miami')) return 'miami';      // Miami-Dade and City of Miami
  return 'broward';
};

const SECTIONS = { miami: MIAMI, boca: BOCA, broward: BROWARD };

const withKeys = (sections) => sections.map(s => ({
  ...s,
  key: `st_${s.num.padStart(2, '0')}`,
  // Repair references offered for this section: its lettered sub-items (7a, 7b, 7c)
  refs: [...new Set(s.lines.map(l => (l.id.match(/^\d+[a-z]/) || [s.num])[0]))],
}));

export const getStructuralSections = (county) => withKeys(SECTIONS[structuralForm(county)]);

// Lead photos: aerial (one) and building elevations (one or more)
export const STRUCTURAL_LEAD = [
  { key: 'st_aerial', title: 'Property Aerial View', lead: true, max: 1 },
  { key: 'st_elev',   title: 'Building Elevation',   lead: true, max: 99 },
];

// ─── Site systems checklist ───────────────────────────────────────────────────
// Asked before the photo review. "Not present" makes the section (or lines) N/A.

const SYSTEMS = [
  { key: 'masonry',     title: 'Masonry bearing walls',                     note: 'Section 6' },
  { key: 'steel',       title: 'Steel framing',                             note: 'Section 8' },
  { key: 'concrete',    title: 'Concrete framing',                          note: 'Section 9' },
  { key: 'wood',        title: 'Wood framing',                              note: 'Section 11' },
  { key: 'threshold',   title: 'Threshold building (façade and structural glazing)', note: 'Section 12 and 10b' },
  { key: 'special',     title: 'Special or unusual features',               note: 'Section 13' },
  { key: 'garage',      title: 'Underground or lower-level parking garage', note: 'Section 14', forms: ['miami', 'boca'] },
  { key: 'balconies',   title: 'Balconies',                                 note: '7b2' },
  { key: 'stairs',      title: 'Stairs or escalators',                      note: '7b3' },
  { key: 'ramps',       title: 'Ramps',                                     note: '7b4' },
  { key: 'parapet',     title: 'Parapet',                                   note: 'Roof' },
  { key: 'mansard',     title: 'Mansard',                                   note: 'Roof' },
  { key: 'rooftop',     title: 'Rooftop equipment (tanks, cooling towers, A/C, signs)', note: 'Roof' },
  { key: 'elevator',    title: 'Elevator',                                  note: 'Steel framing' },
  { key: 'posttension', title: 'Post-tensioned slabs',                      note: 'Boca 9f; context for the AI on other forms' },
];

export const getStructuralSystems = (county) => {
  const form = structuralForm(county);
  return SYSTEMS.filter(s => !s.forms || s.forms.includes(form));
};

// ─── Answers ──────────────────────────────────────────────────────────────────

export const NA_TEXT = 'N/A';
export const NONE_OBSERVED = 'None observed.';

const isAbsent = (siteSystems, key) => !!key && siteSystems?.[key]?.present === false;

// Is this line asked? (its "when" condition, if any, is met)
export const lineActive = (line, answers) => !line.when || answers?.[line.when.id] === line.when.is;

// Answer for each line after defaults, site systems and "when" conditions.
// Returns { [lineId]: value, [lineId + '_txt']: text } plus { locked: { lineId: reason } }.
export const effectiveAnswers = (section, slot, siteSystems) => {
  const out = { ...(slot?.answers || {}) };
  const locked = {};
  section.lines.forEach(l => {
    if (isAbsent(siteSystems, l.naBy)) {
      out[l.id] = l.naValue || (l.kind === 'rating' && l.options.includes('N/A') ? 'N/A' : NA_TEXT);
      locked[l.id] = 'site systems';
      return;
    }
    if (out[l.id] === undefined && l.def !== undefined) out[l.id] = l.def;
  });
  section.lines.forEach(l => {
    if (!locked[l.id] && !lineActive(l, out)) { out[l.id] = NA_TEXT; locked[l.id] = 'not asked'; }
  });
  return { answers: out, locked };
};

// Answers that mean a repair is required — a repair entry must exist
const REPAIR_ANSWER = /(^significant$)|structural repairs required|requires repair|^repairs required$|^needs repair$/i;

export const answerNeedsRepair = (section, answers) =>
  section.lines.some(l => typeof answers[l.id] === 'string' && REPAIR_ANSWER.test(answers[l.id].trim()));

// ─── Photo numbering ──────────────────────────────────────────────────────────
// PHOTO-01 aerial, then elevations, then each section in form order (an N/A
// section is one NOT APPLICABLE panel), then repair photos: "REPAIR ITEM 7a".

export const sectionIsNA = (section, slot, siteSystems) =>
  isAbsent(siteSystems, section.naBy) || !!(slot?.naReason || '').trim();

export const numberStructuralPhotos = (county, slots, siteSystems) => {
  const sections = getStructuralSections(county);
  const cells = [];
  const push = (c) => cells.push({ ...c, label: `PHOTO-${String(cells.length + 1).padStart(2, '0')}` });

  STRUCTURAL_LEAD.forEach(lead => {
    const ids = (slots?.[lead.key]?.photos || []).filter(Boolean);
    if (ids.length === 0) push({ key: lead.key, caption: lead.title, photoId: null });
    ids.forEach(photoId => push({ key: lead.key, caption: lead.title, photoId }));
  });

  sections.forEach(sec => {
    const slot = slots?.[sec.key];
    if (sectionIsNA(sec, slot, siteSystems)) { push({ key: sec.key, caption: sec.title, na: true }); return; }
    const ids = (slot?.photos || []).filter(Boolean);
    if (ids.length === 0) push({ key: sec.key, caption: sec.title, photoId: null });
    ids.forEach(photoId => push({ key: sec.key, caption: sec.title, photoId }));
  });

  sections.forEach(sec => {
    const slot = slots?.[sec.key];
    if (sectionIsNA(sec, slot, siteSystems)) return;
    (slot?.repairs || []).forEach(rep => {
      (rep.photos || []).filter(Boolean).forEach(photoId =>
        push({ key: sec.key, repairId: rep.id, caption: `REPAIR ITEM ${rep.ref || sec.num}`, photoId, repair: true }));
    });
  });

  return cells;
};
